import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { CookiePolicy } from '@ott/cookie-policy';
import { Container } from '@ott/grid';
import { authActions } from '@ott/solar-auth';
import Footer from '@ott/solar-footer';
import Header, { HEADER_THEME_DARK } from '@ott/solar-header';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';
import { RootState } from 'src/redux/store';

import FAQ from 'src/components/blocks/FAQ';
import SupportChat from 'src/components/blocks/SupportChat';

import metrics from 'src/utility/metrics';

import { COOKIE_POLICY_LINK } from 'src/constants/common';

import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';

import Achievements from './blocks/Achievements';
import CircleBlock from './blocks/CircleBlock';
import Loader from './blocks/Loader';
import NFTHeading from './blocks/NFTHeading';
import OrbitsBlock from './blocks/OrbitsBlock';
import { FAQ_DATA } from './constants';

import styles from './RewardsPage.scss';

const RewardsPage = () => {
  const [totalElements, setTotalElements] = useState(-1);
  const [currentlyLoaded, setCurrentlyLoaded] = useState(0);
  const [loader, setLoader] = useState(true);

  const ssr: boolean = useTypedSelector((state: RootState) => state.common.ssr);
  const auth = useTypedSelector(getAuthSelector);
  const isWebview = useIsWebview();
  const dispatch = useDispatch();
  const isAuthorized = auth?.data?.auth;

  const [newMessages, setNewMessages] = useState(0);
  const [isSupportChatVisible, setIsSupportChatVisible] = useState(false);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);

  const history = useHistory();

  const onLoadEvent = () => {
    setCurrentlyLoaded((prevState) => prevState + 1);
  };

  useEffect(() => {
    metrics('SOLAR_LANDING_REWARDS_ENTRY');
  }, []);

  useEffect(() => {
    if (auth.infoStatus.initial) {
      dispatch(authActions.getBasicUserInfo());
    }
  }, []);

  useEffect(() => {
    if (!ssr) {
      const parent = document.getElementById('app') as HTMLDivElement;
      const children = parent.querySelectorAll('svg');

      if (totalElements === -1) {
        setTotalElements(children.length);
      }

      for (let i = 0; i <= children.length - 1; i++) {
        children[i].addEventListener('load', onLoadEvent);
      }
    }
  }, [ssr]);

  useEffect(() => {
    if (totalElements === currentlyLoaded) {
      setTimeout(() => setLoader(false), 5000);
    }
  }, [totalElements, currentlyLoaded]);

  const handleNewMessages = useCallback((count: number) => {
    setNewMessages(count);
  }, []);

  const handleOpenSupportChat = useCallback((isOpen: boolean) => {
    setIsSupportChatOpen(isOpen);
  }, []);

  const onSupportChatVisible = useCallback((isVisible: boolean) => {
    setIsSupportChatVisible(isVisible);
  }, []);

  if (ssr) {
    return null;
  }

  if (loader && !isWebview) {
    return <Loader visible={totalElements !== currentlyLoaded} />;
  }

  return (
    <div className={styles.container}>
      {!isWebview && (
        <>
          <CookiePolicy cookieLink={COOKIE_POLICY_LINK} />
          <Header
            theme={HEADER_THEME_DARK}
            history={history}
            isTransparent={true}
            supportNewMessagesCount={newMessages}
            onOpenSupportChat={handleOpenSupportChat}
            isSupportChatVisible={isSupportChatVisible}
          />
        </>
      )}
      <Container className={styles.content}>
        <NFTHeading />
        <OrbitsBlock />
        <CircleBlock />
        <Achievements />
        <FAQ data={FAQ_DATA} />
      </Container>
      {!isWebview && <Footer />}
      <SupportChat
        isUserAuthorized={isAuthorized}
        isSolar={true}
        isChatButtonHidden={true}
        isOpenFromOutside={isSupportChatOpen}
        onOpenFromOutside={handleOpenSupportChat}
        onReceiveNewMessages={handleNewMessages}
        onCheckAvailability={onSupportChatVisible}
      />
    </div>
  );
};

export default RewardsPage;
